import { useState, useRef } from 'react';
import { Sparkles, Loader2, X, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { supabase } from '@/integrations/supabase/client';
import { runCachedLocalAIRanker } from '@/lib/aiLocalRanker';

interface AIFilterProps {
  type: 'tools' | 'resources';
  data?: any[]; // Pass the current data for local processing
  onResults: (results: AIFilterResult) => void;
  onClose: () => void;
}

interface AIFilterResult {
  results: Array<{
    item: any;
    reason: string;
    relevanceScore: number;
  }>;
  appliedFilters: Record<string, any>;
  intent: string;
  totalFound: number;
}

export function AIFilter({ type, data = [], onResults, onClose }: AIFilterProps) {
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [loadingStep, setLoadingStep] = useState('');
  const abortControllerRef = useRef<AbortController | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim() || isLoading) return;

    setIsLoading(true);
    setError(null);
    setProgress(0);
    setLoadingStep('Processing your request...');

    // Create abort controller for cancellation
    abortControllerRef.current = new AbortController();

    try {
      // Fast progress for local processing
      setProgress(30);
      setLoadingStep('Analyzing with AI...');

      console.log(`🚀 AI Filter: Processing "${query}" for ${type} with ${data.length} items`);

      // Primary: Use local AI ranker (fast, reliable)
      const localResult = await runCachedLocalAIRanker(query.trim(), type, data);
      
      setProgress(80);
      setLoadingStep('Finalizing results...');

      // If we have good local results, use them immediately
      if (localResult.results.length > 0) {
        console.log(`✨ Local AI success: ${localResult.results.length} results`);
        setProgress(100);
        setLoadingStep('Complete!');
        
        // Brief delay for smooth UX
        await new Promise(resolve => setTimeout(resolve, 300));
        onResults(localResult);
        
        // Optional: Try edge function in background as fallback/enhancement
        tryEdgeFunctionFallback(query.trim(), type, localResult);
        return;
      }

      // Fallback: If local ranker has no results, try edge function
      console.log('🔄 No local results, trying edge function...');
      setLoadingStep('Trying advanced search...');
      
      const { data: edgeResult, error: functionError } = await Promise.race([
        supabase.functions.invoke('ai-filter', {
          body: { query: query.trim(), type }
        }),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('timeout')), 15000) // Increased timeout for improved AI
        )
      ]) as any;

      if (functionError) {
        console.warn('Edge function failed:', functionError.message);
        // Still use local result even if it's empty, with helpful message
        setProgress(100);
        setLoadingStep('Complete!');
        onResults({
          ...localResult,
          intent: `No matches found for "${query}". Try a more general search or different keywords.`
        });
        return;
      }

      setProgress(100);
      setLoadingStep('Complete!');
      onResults(edgeResult || localResult);
      
    } catch (err: any) {
      console.error('AI Filter error:', err);
      
      // Always fall back to local result, even if empty
      try {
        const fallbackResult = await runCachedLocalAIRanker(query.trim(), type, data);
        onResults({
          ...fallbackResult,
          intent: `Search encountered an issue. Showing local results for "${query}".`
        });
      } catch (localErr) {
        console.error('Local fallback failed:', localErr);
        setError('Search temporarily unavailable. Please try again or use the regular filters.');
      }
    } finally {
      setIsLoading(false);
      setProgress(0);
      setLoadingStep('');
      abortControllerRef.current = null;
    }
  };

  // Silent background edge function attempt
  const tryEdgeFunctionFallback = async (query: string, type: string, localResult: any) => {
    try {
      const { data: edgeResult } = await supabase.functions.invoke('ai-filter', {
        body: { query, type }
      });
      
      if (edgeResult?.results?.length > localResult.results.length) {
        console.log('🎯 Enhanced AI function found better results, could update in future versions');
        // Could potentially update results live or cache for next search
      }
    } catch (err) {
      console.log('🔇 Enhanced AI function background attempt failed (silent):', err);
    }
  };

  const handleCancel = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };


    return (
    <Card className="p-6 mb-6 border-accent/20 bg-gradient-to-br from-accent-light/5 to-accent-light/10 animate-scale-in">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Sparkles className="h-5 w-5 text-accent animate-pulse" />
          <h3 className="font-heading font-semibold text-text-primary">
            AI Filter
          </h3>
          <Badge variant="secondary" className="bg-accent-light text-accent text-xs animate-scale-in">
            Beta
          </Badge>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="h-6 w-6 p-0 text-text-tertiary hover:text-text-secondary transition-all duration-base hover:scale-110"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <p className="text-sm text-text-secondary mb-4 animate-fade-in">
        Describe what you're looking for in plain language and get AI-curated recommendations tailored to your specific needs.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex space-x-2">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`What kind of ${type} are you looking for?`}
            className="flex-1 transition-all duration-base focus:ring-2 focus:ring-accent/20"
            disabled={isLoading}
          />
          <Button
            type="submit"
            disabled={isLoading || !query.trim()}
            className="bg-accent hover:bg-accent-dark text-white transition-all duration-base hover:scale-105 hover:shadow-lg disabled:hover:scale-100 disabled:hover:shadow-none"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
          </Button>
        </div>

        {isLoading && (
          <div className="space-y-3 p-4 bg-surface/50 rounded-lg border border-accent/10 animate-scale-in">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Clock className="h-4 w-4 text-accent animate-pulse" />
                <span className="text-sm text-text-secondary animate-fade-in">{loadingStep}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCancel}
                className="text-text-tertiary hover:text-error text-xs transition-colors duration-fast hover:scale-105"
              >
                Cancel
              </Button>
            </div>
            <Progress value={progress} className="h-2 transition-all duration-slow" />
                <p className="text-xs text-text-tertiary animate-fade-in">
                  This usually takes 2-3 seconds. Be specific for best results.
                </p>
          </div>
        )}

        {error && (
          <div className="text-sm text-error bg-error-light/20 border border-error-light rounded-md p-3 animate-scale-in">
            {error}
          </div>
        )}

      </form>
    </Card>
  );
}